import React, { useCallback, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'

import { selectBusinessEventPollingEnabled, setBusinessEvents } from '../../../store/businessEvent/businessEvent'
import { useLazyGetAllTasksQuery } from '../../../store/meetingSupport/meetingSupport'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { useLazyGetAllBusinessEventsQuery } from '../../../store/ai/businessEvent'
import { selectTaskPollingEnabled, setTasks } from '../../../store/task/task'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { BusinessEventSourceType } from '../../../models/BusinessEvent'

export const EmailHubPoller: React.FC = () => {
  usePollEmailThreadTasks()
  usePollEmailBusinessEvents()

  return null
}

const usePollEmailThreadTasks = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const taskPollingEnabled = useAppSelector(selectTaskPollingEnabled)

  const { threadId = null } = useParams()

  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  const getTasks = useCallback(async () => {
    if (!threadId || !emailIntegration) {
      return
    }
    const { serviceId: emailConnector } = emailIntegration
    try {
      const filters = { type: 'email', emailConnector, threadId }
      const { data: tasksResponse } = await triggerGetTasks({ accountId, filters })
      if (!tasksResponse?.data) {
        return
      }
      dispatch(setTasks(tasksResponse.data))
    } catch (e) {
      console.log(e)
    }
  }, [accountId, dispatch, emailIntegration, threadId, triggerGetTasks])

  const { start: startEmailThreadTasksPolling, stop: stopEmailThreadTasksPolling } = usePollingEffect(getTasks, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (taskPollingEnabled) {
      startEmailThreadTasksPolling()
    } else {
      stopEmailThreadTasksPolling()
    }
  }, [startEmailThreadTasksPolling, stopEmailThreadTasksPolling, taskPollingEnabled])
}

const usePollEmailBusinessEvents = () => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const businessEventPollingEnabled = useAppSelector(selectBusinessEventPollingEnabled)

  const { threadId = null } = useParams()

  const [triggerGetBusinessEvents] = useLazyGetAllBusinessEventsQuery()

  const fetchBusinessEvents = useCallback(async () => {
    if (!threadId || !emailIntegration) {
      return
    }
    const { serviceId: emailConnector } = emailIntegration
    try {
      const { data: businessEvents } = await triggerGetBusinessEvents({
        accountId,
        sourceType: BusinessEventSourceType.EMAIL,
        sourceKey: `${emailConnector}|${threadId}`,
        partialKeyMatch: true,
      }).unwrap()
      if (!businessEvents.length) {
        return
      }
      dispatch(setBusinessEvents(businessEvents))
    } catch (e) {
      console.error(e)
    }
  }, [accountId, dispatch, emailIntegration, threadId, triggerGetBusinessEvents])

  const { start: startBusinessEventPolling, stop: stopBusinessEventPolling } = usePollingEffect(fetchBusinessEvents, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (businessEventPollingEnabled) {
      startBusinessEventPolling()
    } else {
      stopBusinessEventPolling()
    }
  }, [businessEventPollingEnabled, startBusinessEventPolling, stopBusinessEventPolling])
}
